import { useCallback } from 'react';

import {
  StoreCategoriesResponse,
  useListCategories,
  useListStoreItems,
} from '@givebacks/api/store';
import { useBreakPoints } from 'core/Hooks';
import { useSearchParams } from 'react-router-dom';
import { ButtonGroup, ButtonMantine, ConditionalWrapper, Grid, ScrollArea } from 'ui';

import classes from './CategoryButtons.module.css';

const selectCategoryButtons = (data?: StoreCategoriesResponse) => {
  return (
    data?.categories.map((category) => ({
      label: category.name,
      value: category.uuid,
    })) ?? []
  );
};

const CategoryButtons = ({ storeId }: { storeId: string }) => {
  const { isSmallScreen } = useBreakPoints();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeCategory = searchParams.get('category');

  const { data: categories } = useListCategories({
    causeId: storeId ?? '',
    queryOptions: {
      searches: {
        hidden: { value: false },
      },
    },
    select: selectCategoryButtons,
  });

  const { data: hasMembershipProducts } = useListStoreItems({
    queryOptions: {
      options: {
        total: true,
      },
      searches: {
        type: {
          value: 'StoreItems::Membership',
        },
        live: {
          value: true,
        },
      },
    },
    causeId: storeId,
    select: (data) => data?.store_items.length > 0,
  });

  const handleCategoryClick = useCallback(
    (category: string | null) => {
      if (category) {
        setSearchParams({ category });
      } else {
        searchParams.delete('category');
        setSearchParams(searchParams);
      }
    },
    [searchParams, setSearchParams]
  );

  return (
    <ConditionalWrapper
      condition={!!isSmallScreen}
      wrapper={(children) => (
        <ScrollArea
          scrollbarSize={0}
          classNames={{
            root: classes.CategoryButtons,
            viewport: classes.CategoryButtons__viewport,
          }}
          scrollbars="x"
        >
          {children}
        </ScrollArea>
      )}
    >
      <ButtonGroup value={{ value: activeCategory ?? undefined }} nowrap={isSmallScreen}>
        <Grid.Col span="content">
          <ButtonMantine
            size="sm"
            onClick={() => {
              handleCategoryClick(null);
            }}
            data-active={activeCategory === null}
          >
            All
          </ButtonMantine>
        </Grid.Col>

        {hasMembershipProducts && (
          <Grid.Col span="content">
            <ButtonMantine
              size="sm"
              onClick={() => {
                handleCategoryClick('memberships');
              }}
              data-active={activeCategory === 'memberships'}
            >
              Memberships
            </ButtonMantine>
          </Grid.Col>
        )}

        {categories?.map((category) => (
          <Grid.Col span="content" key={category.value}>
            <ButtonMantine
              size="sm"
              onClick={() => {
                handleCategoryClick(category.value);
              }}
              data-active={category.value === activeCategory}
            >
              {category.label}
            </ButtonMantine>
          </Grid.Col>
        ))}
      </ButtonGroup>
    </ConditionalWrapper>
  );
};

export { CategoryButtons };
